import { useQuery } from '@tanstack/react-query'
import { AlertCircle, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { usePermission } from '@/hooks/usePermission'
import { getRoles } from '@/services/api/permission.service'
import { getWorkspaceUsers } from '@/services/api/task.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { cn } from '@/utils/cn'

import { AvailableVariables } from './TriggerFilterPickers'

export interface SendNotificationMetadata {
  channels?: string[]
  title?: string
  body?: string
  recipientRoleIds?: string[]
  recipientUserIds?: string[]
  notifyRecordCreator?: boolean
  notifyActor?: boolean
}

export const NOTIFICATION_TITLE_MAX = 120
export const NOTIFICATION_BODY_MAX = 1000

/** The same rules the server applies when the automation is saved; returns field-keyed messages. */
export function validateSendNotification(metadata: SendNotificationMetadata): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!(metadata.channels ?? []).length) errors.channels = 'Choose at least one channel'
  const title = metadata.title?.trim() ?? ''
  if (!title) errors.title = 'Title is required'
  else if (title.length > NOTIFICATION_TITLE_MAX) errors.title = `Title can be at most ${NOTIFICATION_TITLE_MAX} characters`
  const body = metadata.body?.trim() ?? ''
  if (!body) errors.body = 'Message is required'
  else if (body.length > NOTIFICATION_BODY_MAX) errors.body = `Message can be at most ${NOTIFICATION_BODY_MAX} characters`
  const hasRecipients = (metadata.recipientRoleIds ?? []).length > 0
    || (metadata.recipientUserIds ?? []).length > 0
    || !!metadata.notifyRecordCreator
    || !!metadata.notifyActor
  if (!hasRecipients) errors.recipients = 'Choose who to notify'
  return errors
}

const CHANNELS = [
  { value: 'IN_APP', label: 'In-app' },
  { value: 'PUSH', label: 'Push (mobile app and browser)' },
]

interface Props {
  metadata: SendNotificationMetadata
  errors?: Record<string, string>
  onChange: (metadata: SendNotificationMetadata) => void
  recordGroupId: string
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p className="flex items-center gap-1 text-xs text-rose-600">
      <AlertCircle className="h-3 w-3 shrink-0" /> {message}
    </p>
  )
}

const toggled = (list: string[], id: string) => (list.includes(id) ? list.filter((v) => v !== id) : [...list, id])

/** Editor of the SEND_NOTIFICATION action: channels, text with variables, and who is notified. */
export function SendNotificationFields({ metadata, errors, onChange, recordGroupId }: Props) {
  const { canAnywhere } = usePermission()
  const canListRoles = canAnywhere(PERMISSION_KEYS.ROLE_VIEW)
  const { data: roles = [] } = useQuery({ queryKey: ['roles'], queryFn: getRoles, enabled: canListRoles })
  const { data: users = [] } = useQuery({ queryKey: ['workspace-users', 'light'], queryFn: getWorkspaceUsers })
  const [search, setSearch] = useState('')

  const channels = metadata.channels ?? []
  const roleIds = metadata.recipientRoleIds ?? []
  const userIds = useMemo(() => metadata.recipientUserIds ?? [], [metadata.recipientUserIds])
  const set = (patch: Partial<SendNotificationMetadata>) => onChange({ ...metadata, ...patch })

  const usersById = useMemo(() => new Map(users.map((u) => [u.userId, u])), [users])
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    return users
      .filter((u) => !userIds.includes(u.userId))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8)
  }, [users, userIds, search])

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          Channels <span className="normal-case tracking-normal text-rose-500">*</span>
        </Label>
        <div className="flex flex-wrap gap-4">
          {CHANNELS.map((channel) => (
            <label key={channel.value} className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={channels.includes(channel.value)}
                onCheckedChange={() => set({ channels: toggled(channels, channel.value) })}
              />
              {channel.label}
            </label>
          ))}
        </div>
        <FieldError message={errors?.channels} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="notification-title" className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          Title <span className="normal-case tracking-normal text-rose-500">*</span>
        </Label>
        <Input
          id="notification-title"
          placeholder="e.g. {{tag.label}} lead: {{record.title}}"
          value={metadata.title ?? ''}
          maxLength={NOTIFICATION_TITLE_MAX + 40}
          aria-invalid={errors?.title ? true : undefined}
          onChange={(e) => set({ title: e.target.value })}
          className={errors?.title ? 'border-rose-300' : ''}
        />
        <FieldError message={errors?.title} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="notification-body" className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          Message <span className="normal-case tracking-normal text-rose-500">*</span>
        </Label>
        <Textarea
          id="notification-body"
          rows={3}
          placeholder="e.g. {{actor.name}} tagged {{record.title}}"
          value={metadata.body ?? ''}
          aria-invalid={errors?.body ? true : undefined}
          onChange={(e) => set({ body: e.target.value })}
          className={errors?.body ? 'border-rose-300' : ''}
        />
        <FieldError message={errors?.body} />
        <AvailableVariables recordGroupId={recordGroupId} />
      </div>

      <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3">
        <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          Who is notified <span className="normal-case tracking-normal text-rose-500">*</span>
        </Label>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-neutral-600">Roles</p>
          {!canListRoles ? (
            <p className="text-xs italic text-neutral-400">You need permission to view roles to choose them here.</p>
          ) : roles.length === 0 ? (
            <p className="text-xs italic text-neutral-400">No roles.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {roles.filter((r) => r.active).map((role) => (
                <button
                  key={role.id}
                  type="button"
                  aria-pressed={roleIds.includes(role.id)}
                  onClick={() => set({ recipientRoleIds: toggled(roleIds, role.id) })}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition-colors',
                    roleIds.includes(role.id)
                      ? 'border-primary-500 bg-primary-50 font-semibold text-primary-800'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-100'
                  )}
                >
                  {role.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-neutral-600">People</p>
          {userIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {userIds.map((id) => (
                <span key={id} className="inline-flex items-center gap-1 rounded-full border border-primary-500 bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-800">
                  {usersById.get(id)?.name ?? 'Unknown member'}
                  <button
                    type="button"
                    aria-label={`Remove ${usersById.get(id)?.name ?? 'member'}`}
                    onClick={() => set({ recipientUserIds: userIds.filter((u) => u !== id) })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <Input
            aria-label="Search people"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
          />
          {search.trim() && (
            <ul className="max-h-40 divide-y overflow-y-auto rounded-md border bg-white text-sm">
              {matches.length === 0 ? (
                <li className="px-3 py-2 text-xs italic text-neutral-400">No matching people</li>
              ) : (
                matches.map((user) => (
                  <li key={user.userId}>
                    <button
                      type="button"
                      className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-neutral-50"
                      onClick={() => {
                        set({ recipientUserIds: [...userIds, user.userId] })
                        setSearch('')
                      }}
                    >
                      <span className="font-medium">{user.name}</span>
                      <span className="text-xs text-neutral-500">{user.email}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={!!metadata.notifyRecordCreator}
              onCheckedChange={(checked) => set({ notifyRecordCreator: checked === true })}
            />
            The record's creator
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={!!metadata.notifyActor}
              onCheckedChange={(checked) => set({ notifyActor: checked === true })}
            />
            The person who triggered it
          </label>
        </div>
        <FieldError message={errors?.recipients} />
        <p className="text-xs text-neutral-500">Only people who can view the record are notified.</p>
      </div>
    </div>
  )
}
